import React, { useState, useEffect } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Grid,
    LinearProgress,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
    Alert,
    Chip,
} from '@mui/material';
import { IconMail, IconEye, IconDeviceFloppy, IconRefresh } from '@tabler/icons';
import MainCard from 'ui-component/cards/MainCard';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const GET_REJECT_EMAIL_TEMPLATE = gql`
    query GetRejectEmailTemplate($eventId: String!, $role: ROLE!) {
        rejectEmailTemplate(eventId: $eventId, role: $role) {
            subject
            body
            isHtml
        }
        invitationVariables(eventId: $eventId, role: $role)
    }
`;

const SAVE_REJECT_EMAIL_TEMPLATE = gql`
    mutation SaveRejectEmailTemplate(
        $eventId: String!
        $role: ROLE!
        $subject: String!
        $body: String!
        $isHtmlMode: Boolean!
    ) {
        saveRejectEmailTemplate(
            eventId: $eventId
            role: $role
            subject: $subject
            body: $body
            isHtmlMode: $isHtmlMode
        )
    }
`;

const SEND_TEST_REJECT_EMAIL = gql`
    mutation SendTestRejectEmail($eventId: String!, $role: ROLE!, $testEmail: String!) {
        sendTestRejectEmail(eventId: $eventId, role: $role, testEmail: $testEmail)
    }
`;

const PREVIEW_EMAIL_TEMPLATE = gql`
    mutation PreviewEmailTemplate(
        $eventId: String!
        $role: ROLE!
        $subject: String!
        $body: String!
        $templateType: String!
    ) {
        previewEmailTemplate(
            eventId: $eventId
            role: $role
            subject: $subject
            body: $body
            templateType: $templateType
        ) {
            subject
            body
        }
    }
`;

export default function RejectEmailSetup({ eventId, role }) {
    const [subject, setSubject] = useState('');
    const [htmlBody, setHtmlBody] = useState('');
    const [richTextBody, setRichTextBody] = useState('');
    const [testEmail, setTestEmail] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [previewData, setPreviewData] = useState({});
    const [message, setMessage] = useState({ type: '', text: '' });
    const [isHtmlMode, setIsHtmlMode] = useState(true);

    const { data, loading, error, refetch } = useQuery(GET_REJECT_EMAIL_TEMPLATE, {
        variables: { eventId, role },
        errorPolicy: 'all',
        onError: (e) => {
            console.error('Error fetching reject email template:', e);
            setMessage({
                type: 'error',
                text: 'Failed to load reject email template. Please refresh the page.',
            });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        },
    });

    const [saveTemplate, { loading: saving }] = useMutation(SAVE_REJECT_EMAIL_TEMPLATE, {
        onCompleted: (data) => {
            setMessage({ type: 'success', text: data.saveRejectEmailTemplate });
            refetch();
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        },
        onError: (e) => {
            setMessage({ type: 'error', text: e.message || 'Failed to save template' });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        },
    });

    const [sendTestEmail, { loading: sendingTest }] = useMutation(SEND_TEST_REJECT_EMAIL, {
        onCompleted: (data) => {
            setMessage({ type: 'success', text: data.sendTestRejectEmail });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        },
        onError: (e) => {
            setMessage({ type: 'error', text: e.message || 'Failed to send test email' });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        },
    });

    const [previewEmail, { loading: previewing }] = useMutation(PREVIEW_EMAIL_TEMPLATE, {
        onCompleted: (data) => {
            setPreviewData({
                subject: data.previewEmailTemplate.subject,
                body: data.previewEmailTemplate.body,
                isHtml: isHtmlMode,
            });
            setShowPreview(true);
        },
        onError: (e) => {
            setMessage({ type: 'error', text: e.message || 'Failed to preview email' });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        },
    });

    useEffect(() => {
        try {
            if (data?.rejectEmailTemplate) {
                const template = data.rejectEmailTemplate;
                setSubject(template.subject || '');
                if (template.isHtml) {
                    setHtmlBody(template.body || '');
                    setRichTextBody('');
                } else {
                    setRichTextBody(template.body || '');
                    setHtmlBody('');
                }
                setIsHtmlMode(template.isHtml !== false);
            }
        } catch (error) {
            console.error('Error processing reject email template data:', error);
            setMessage({
                type: 'error',
                text: 'Error processing template data. Please refresh the page.',
            });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        }
    }, [data]);

    const handleSave = () => {
        if (!subject.trim() || (!htmlBody.trim() && !richTextBody.trim())) {
            setMessage({ type: 'error', text: 'Please fill in both subject and body' });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
            return;
        }

        const body = isHtmlMode ? htmlBody : richTextBody;
        saveTemplate({
            variables: {
                eventId,
                role,
                subject: subject.trim(),
                body: body.trim(),
                isHtmlMode,
            },
        });
    };

    const handlePreview = async () => {
        if (!subject.trim() || (!htmlBody.trim() && !richTextBody.trim())) {
            setMessage({ type: 'error', text: 'Please fill in both subject and body to preview' });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
            return;
        }

        const body = isHtmlMode ? htmlBody : richTextBody;

        try {
            await previewEmail({
                variables: {
                    eventId,
                    role,
                    subject,
                    body,
                    templateType: 'reject',
                },
            });
        } catch (error) {
            console.error('Failed to preview email:', error);
        }
    };

    const handleSendTest = () => {
        if (!testEmail.trim()) {
            setMessage({ type: 'error', text: 'Please enter a test email address' });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
            return;
        }

        if (!subject.trim() || (!htmlBody.trim() && !richTextBody.trim())) {
            setMessage({
                type: 'error',
                text: 'Please fill in both subject and body before sending test',
            });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
            return;
        }

        sendTestEmail({
            variables: {
                eventId,
                role,
                testEmail: testEmail.trim(),
            },
        });
    };

    const renderPreview = () => (
        <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1, bgcolor: '#f9f9f9' }}>
            <Typography variant="h6" gutterBottom>
                Email Preview
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                    Subject:
                </Typography>
                <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                    {previewData.subject}
                </Typography>
            </Box>

            <Box>
                <Typography variant="subtitle2" color="textSecondary">
                    Body:
                </Typography>
                <Box
                    sx={{
                        mt: 1,
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        bgcolor: 'white',
                        minHeight: '200px',
                    }}
                >
                    {previewData.isHtml ? (
                        <Box
                            dangerouslySetInnerHTML={{ __html: previewData.body }}
                            sx={{
                                '& h1, & h2, & h3, & h4, & h5, & h6': {
                                    color: 'text.primary',
                                    fontWeight: 600,
                                    mb: 1,
                                },
                                '& p': { mb: 1 },
                                '& a': { color: 'primary.main' },
                                '& strong': { fontWeight: 600 },
                                '& em': { fontStyle: 'italic' },
                                '& ul, & ol': { pl: 2, mb: 1 },
                                '& li': { mb: 0.5 },
                                '& table': { borderCollapse: 'collapse', width: '100%' },
                                '& td, & th': { border: '1px solid #ddd', padding: '8px' },
                            }}
                        />
                    ) : (
                        <Box
                            dangerouslySetInnerHTML={{ __html: previewData.body }}
                            sx={{
                                '& h1, & h2, & h3, & h4, & h5, & h6': {
                                    color: 'text.primary',
                                    fontWeight: 600,
                                    mb: 1,
                                },
                                '& p': { mb: 1 },
                                '& a': { color: 'primary.main' },
                                '& strong': { fontWeight: 600 },
                                '& em': { fontStyle: 'italic' },
                                '& ul, & ol': { pl: 2, mb: 1 },
                                '& li': { mb: 0.5 },
                            }}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    );

    if (loading) {
        return (
            <MainCard>
                <Box sx={{ p: 3 }}>
                    <LinearProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        Loading reject email template...
                    </Typography>
                </Box>
            </MainCard>
        );
    }

    if (error) {
        return (
            <MainCard>
                <Box sx={{ p: 3 }}>
                    <Alert severity="error">
                        Error loading reject email template: {error.message}
                    </Alert>
                </Box>
            </MainCard>
        );
    }

    return (
        <MainCard>
            <Box sx={{ p: 3 }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" gutterBottom>
                        Reject Email Setup
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Configure the email template that will be sent to users when you reject
                        their interest. This email is optional - you can choose whether to send it
                        when rejecting invitations.
                    </Typography>
                </Box>

                {message.text && (
                    <Alert
                        severity={message.type}
                        onClose={() => setMessage({ type: '', text: '' })}
                    >
                        {message.text}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Card>
                            <CardContent>
                                <Stack spacing={3}>
                                    <TextField
                                        fullWidth
                                        label="Email Subject"
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        placeholder="Enter email subject..."
                                        helperText="Use variables like $Full Name, $Email, etc."
                                    />

                                    <Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                mb: 2,
                                            }}
                                        >
                                            <Typography variant="h6">Email Body</Typography>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                <Typography variant="body2">Rich Text</Typography>
                                                <Switch
                                                    checked={isHtmlMode}
                                                    onChange={(e) =>
                                                        setIsHtmlMode(e.target.checked)
                                                    }
                                                />
                                                <Typography variant="body2">HTML</Typography>
                                            </Box>
                                        </Box>

                                        {isHtmlMode ? (
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={12}
                                                value={htmlBody}
                                                onChange={(e) => setHtmlBody(e.target.value)}
                                                placeholder="<html><body><h1>Hello $Full Name</h1></body></html>"
                                                helperText="Write your HTML code here. Use the variables listed above for dynamic content."
                                                sx={{
                                                    '& .MuiInputBase-input': {
                                                        fontFamily: 'monospace',
                                                        fontSize: '14px',
                                                    },
                                                }}
                                            />
                                        ) : (
                                            <Paper sx={{ border: '1px solid #e0e0e0' }}>
                                                <ReactQuill
                                                    theme="snow"
                                                    value={richTextBody}
                                                    onChange={setRichTextBody}
                                                    modules={{
                                                        toolbar: [
                                                            [{ header: [1, 2, 3, false] }],
                                                            [
                                                                'bold',
                                                                'italic',
                                                                'underline',
                                                                'strike',
                                                            ],
                                                            [
                                                                { list: 'ordered' },
                                                                { list: 'bullet' },
                                                            ],
                                                            [{ color: [] }, { background: [] }],
                                                            ['link', 'image'],
                                                            ['clean'],
                                                        ],
                                                    }}
                                                    style={{ minHeight: '300px' }}
                                                />
                                            </Paper>
                                        )}
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Available Variables
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                    Use these variables in your email template. They will be
                                    automatically replaced with actual values when sending reject
                                    emails.
                                </Typography>
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        User Data Variables:
                                    </Typography>
                                    <Stack spacing={1}>
                                        {data?.invitationVariables?.map((variable, index) => (
                                            <Chip
                                                key={index}
                                                label={`$${variable}`}
                                                size="small"
                                                variant="outlined"
                                                title={`User's ${variable} field`}
                                                onClick={() => {
                                                    const textField = document.querySelector(
                                                        'input[placeholder="Enter email subject..."]'
                                                    );
                                                    if (textField) {
                                                        textField.focus();
                                                        const start = textField.selectionStart;
                                                        const end = textField.selectionEnd;
                                                        const newValue =
                                                            subject.substring(0, start) +
                                                            `$${variable}` +
                                                            subject.substring(end);
                                                        setSubject(newValue);
                                                    }
                                                }}
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    sx={{ mt: 1, display: 'block' }}
                                >
                                    <strong>Note:</strong> Reject emails are optional. You can
                                    choose whether to send them when rejecting invitations. If no
                                    reject email template is configured, invitations will be
                                    rejected without sending any email.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Action Buttons */}
                <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                    <Button
                        variant="contained"
                        startIcon={<IconDeviceFloppy size={20} />}
                        onClick={handleSave}
                        disabled={saving}
                    >
                        Save Template
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<IconEye size={20} />}
                        onClick={handlePreview}
                        disabled={previewing}
                    >
                        {previewing ? 'Generating Preview...' : 'Preview Email'}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<IconRefresh size={20} />}
                        onClick={() => refetch()}
                    >
                        Refresh
                    </Button>
                </Stack>

                {/* Preview Section */}
                {showPreview && renderPreview()}

                {/* Test Email Section */}
                <Divider sx={{ my: 3 }} />
                <Box>
                    <Typography variant="h6" gutterBottom>
                        Send Test Email
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Send a test email to verify your template works correctly.
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                            placeholder="Enter test email address"
                            value={testEmail}
                            onChange={(e) => setTestEmail(e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ minWidth: '300px' }}
                        />
                        <Button
                            variant="contained"
                            startIcon={<IconMail size={20} />}
                            onClick={handleSendTest}
                            disabled={!testEmail || sendingTest}
                        >
                            Send Test Email
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </MainCard>
    );
}
